import React, { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import { ArrowLeft } from 'tabler-icons-react'
import ExperiencePreviewBar from '../components/ExperiencePreviewBar'
import useExperiencePreview from '../hooks/useExperiencePreview'
import { formatDate } from './myExperienceUtils'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

const getNonEmptyTrimmedValue = (...values) => {
	for (const value of values) {
		if (typeof value !== 'string') continue
		const normalized = value.trim()
		if (normalized) return normalized
	}
	return null
}

const toPreviewPass = (data) => {
	if (!data) return data
	const { checkIn = {}, experience = {} } = data

	return {
		...checkIn,
		experiencePrimaryColor: experience.primaryColor,
		experienceTextColor: experience.textColor,
		experienceLabelColor: experience.labelColor,
		experienceStripImage: experience.stripImage,
		experienceName: experience.name || null,
		shortName: experience.shortName || null,
		stationLabelOverride: experience.stationLabelOverride || null,
		orgName: experience.org?.name || null,
		orgLogo: experience.org?.logo || null,
	}
}

const CheckInPassPage = ({
	previewMode = false,
	mockData = null,
	experiencePreview = false,
}) => {
	const { id, experienceId } = useParams()
	const [fetchedCheckIn, setFetchedCheckIn] = useState(
		previewMode ? mockData : undefined,
	)
	const {
		data: experiencePreviewData,
		previewOptions,
		selectedStationId,
		selectedSegmentId,
		setStationId,
		setSegmentId,
	} = useExperiencePreview(experienceId, experiencePreview)
	const checkIn = previewMode
		? mockData
		: experiencePreview
			? toPreviewPass(experiencePreviewData)
			: fetchedCheckIn
	const passRef = useRef()
	const resolvedExperienceName =
		getNonEmptyTrimmedValue(checkIn?.shortName, checkIn?.experienceName) ||
		'Experience'
	const resolvedTravelerPhone = getNonEmptyTrimmedValue(checkIn?.travelerPhone)
	const resolvedStationName = getNonEmptyTrimmedValue(checkIn?.station?.name)
	const isIOS =
		process.env.NODE_ENV === 'development' ||
		/iphone|ipad|ipod/i.test(navigator.userAgent)

	useEffect(() => {
		if (previewMode) {
			setFetchedCheckIn(mockData)
			return undefined
		}
		if (experiencePreview) return undefined

		const controller = new AbortController()
		fetch(`${BASE_URL}/api/passes/${id}`, { signal: controller.signal })
			.then(async (res) => {
				if (!res.ok) throw new Error('Pass lookup failed')
				return res.json()
			})
			.then(setFetchedCheckIn)
			.catch((error) => {
				if (error.name !== 'AbortError') setFetchedCheckIn(null)
			})

		return () => controller.abort()
	}, [experiencePreview, previewMode, id, mockData])

	const previewBar = experiencePreview ? (
		<ExperiencePreviewBar
			stations={previewOptions.stations || []}
			segments={previewOptions.segments || []}
			selectedStationId={selectedStationId}
			selectedSegmentId={selectedSegmentId}
			onStationChange={setStationId}
			onSegmentChange={setSegmentId}
		/>
	) : null

	if (!previewMode && checkIn === undefined) {
		return (
			<>
				{previewBar}
				<p style={{ padding: '2rem' }}>Loading pass...</p>
			</>
		)
	}

	if (!previewMode && checkIn === null) {
		return (
			<>
				{previewBar}
				<div style={styles.page}>
					<div style={styles.container}>
						<h1 style={styles.heading}>Pass Not Found</h1>
						<p style={styles.subtext}>
							We couldn't find a check-in with that ID.
						</p>
						<div style={styles.section}>
							<p style={styles.data}>
								Please check the link or contact experience support if you
								believe this is an error.
							</p>
						</div>
					</div>
				</div>
			</>
		)
	}

	const resolvedPassBackgroundColor = checkIn?.experiencePrimaryColor || '#fff'
	const resolvedTextColor = checkIn?.experienceTextColor || '#000000'
	const resolvedLabelColor = checkIn?.experienceLabelColor || resolvedTextColor
	const previewQuery = new URLSearchParams()
	if (selectedStationId) previewQuery.set('stationId', selectedStationId)
	if (selectedSegmentId) previewQuery.set('segmentId', selectedSegmentId)
	const experiencePortalHref = experiencePreview
		? `/myexperience/preview/${experienceId}${
				previewQuery.toString() ? `?${previewQuery.toString()}` : ''
			}`
		: `/myexperience/${id}`

	return (
		<>
			{previewBar}
			<div style={previewMode ? styles.previewWrapper : styles.page}>
			<style>{`
				@media print {
					.no-print {
						display: none !important;
					}
				}
			`}</style>

			{!previewMode && (
				<nav className='no-print' style={styles.navigation}>
					<Link
						to={experiencePortalHref}
						style={styles.backLink}
					>
						<ArrowLeft size={20} />
						To experience portal{experiencePreview ? ' preview' : ''}
					</Link>
				</nav>
			)}

			<div
				ref={passRef}
				style={{
					...styles.passContainer,
					backgroundColor: resolvedPassBackgroundColor,
					color: resolvedTextColor,
				}}
			>
				<div style={styles.header}>
					{checkIn?.orgLogo && (
						<img src={checkIn?.orgLogo} alt='Org Logo' style={styles.logo} />
					)}
					<h1 style={{ ...styles.experienceName, color: resolvedLabelColor }}>
						{resolvedExperienceName}
					</h1>
				</div>

				{checkIn?.experienceStripImage && (
					<img
						src={checkIn?.experienceStripImage}
						alt='Pass Strip'
						style={styles.stripImage}
					/>
				)}

				<div style={styles.fields}>
					<div style={styles.row}>
						<div>
							<p style={{ ...styles.label, color: resolvedLabelColor }}>Name</p>
							<p style={styles.value}>{checkIn.travelerName}</p>
						</div>
					</div>

					<div style={styles.row}>
						<div>
							<p style={{ ...styles.label, color: resolvedLabelColor }}>
								Email
							</p>
							<p style={styles.value}>{checkIn.travelerEmail}</p>
						</div>
						{resolvedTravelerPhone && (
							<div style={{ textAlign: 'right' }}>
								<p style={{ ...styles.label, color: resolvedLabelColor }}>
									Phone
								</p>
								<p style={styles.value}>{resolvedTravelerPhone}</p>
							</div>
						)}
					</div>

					<div style={styles.row}>
						{resolvedStationName && (
							<div>
								<p
									style={{ ...styles.label, color: resolvedLabelColor }}
								>{`Your ${
									checkIn.stationLabelOverride
										? checkIn.stationLabelOverride
										: 'Check-In Location'
								}`}</p>
								<p style={styles.value}>{resolvedStationName}</p>
							</div>
						)}
						<div style={{ textAlign: resolvedStationName ? 'right' : 'left' }}>
							<p style={{ ...styles.label, color: resolvedLabelColor }}>
								Your Check-In Date
							</p>
							<p style={styles.value}>
								{formatDate(checkIn.scheduledCheckInDate)}
							</p>
						</div>
					</div>
				</div>

				<div>
					<div style={styles.qrContainer}>
						<div style={styles.qrBackground}>
							<QRCodeCanvas
								value={checkIn.id}
								size={180}
								level='H'
								style={styles.qrCode}
							/>
						</div>
					</div>
				</div>
			</div>

			{isIOS && !previewMode && !experiencePreview && (
				<div className='no-print' style={styles.buttonSection}>
					<a
						href={`${BASE_URL}/api/passes/${id}/apple`}
						style={styles.appleButton}
						download
					>
						Add to Apple Wallet
					</a>
				</div>
			)}
			</div>
		</>
	)
}

const styles = {
	fields: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.75rem',
		marginBottom: '1.5rem',
	},
	row: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	value: {
		fontSize: '1rem',
		fontWeight: 500,
		margin: 0,
	},
	valueRight: {
		fontSize: '1rem',
		fontWeight: 500,
		margin: 0,
		textAlign: 'right',
	},
	page: {
		backgroundColor: '#f7f7f7',
		minHeight: '100vh',
		padding: '2rem 1rem',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	container: {
		backgroundColor: '#ffffff',
		width: '100%',
		maxWidth: '500px',
		padding: '2rem',
		borderRadius: '12px',
		boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
		color: '#000000',
	},
	subtext: {
		textAlign: 'center',
		fontSize: '0.9rem',
		fontStyle: 'italic',
		marginBottom: '2rem',
	},
	label: {
		marginBottom: '0.25rem',
		fontWeight: 'bold',
	},
	data: {
		marginBottom: '1rem',
	},
	qrContainer: {
		display: 'flex',
		justifyContent: 'center',
	},
	qrBackground: {
		backgroundColor: '#ffffff',
		padding: '8px',
		borderRadius: '16px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	qrCode: {
		width: '180px',
		height: '180px',
	},
	qrId: {
		marginTop: '0.5rem',
		fontSize: '0.75rem',
		color: '#000000',
		textAlign: 'center',
	},
	buttonSection: {
		width: '100%',
		maxWidth: '500px',
		marginTop: '2rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		padding: '0 1rem',
	},
	button: {
		width: '100%',
		padding: '0.75rem',
		fontSize: '1rem',
		backgroundColor: '#545454',
		color: '#fff',
		border: 'none',
		borderRadius: '8px',
		fontWeight: 'bold',
		cursor: 'pointer',
		transition: 'background 0.3s ease',
	},
	stripImage: {
		width: 'calc(100% + 2.5rem)',
		marginLeft: '-1.25rem',
		marginRight: '-1.25rem',
		objectFit: 'cover',
		marginBottom: '1.5rem',
		display: 'block',
	},
	passContainer: {
		width: '100%',
		maxWidth: '500px',
		borderRadius: '20px',
		padding: '1.5rem 1.25rem',
		boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
		boxSizing: 'border-box',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		gap: '1rem',
		marginBottom: '1.5rem',
	},
	logo: {
		width: '48px',
		height: '48px',
		objectFit: 'contain',
		borderRadius: '6px',
	},
	experienceName: {
		fontSize: '1.75rem',
		fontWeight: 600,
		margin: 0,
	},
	appleButton: {
		width: '100%',
		padding: '0.75rem',
		fontSize: '1rem',
		backgroundColor: '#000000',
		color: '#fff',
		border: 'none',
		borderRadius: '8px',
		fontWeight: 'bold',
		cursor: 'pointer',
		textDecoration: 'none',
		textAlign: 'center',
		transition: 'background 0.3s ease',
	},
	previewWrapper: {
		backgroundColor: '#f7f7f7',
		padding: '1rem',
		display: 'flex',
		justifyContent: 'center',
	},
	navigation: {
		width: '100%',
		maxWidth: '500px',
		marginBottom: '1rem',
	},
	backLink: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '.4rem',
		color: '#343846',
		fontSize: '.9rem',
		fontWeight: 700,
		textDecoration: 'none',
	},
}

export default CheckInPassPage
